import React, { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Card } from "@/components/ui/card";
import FunderAnalysisDashboard from "@/components/underwriting/FunderAnalysisDashboard";
import { borrowerService } from "@/services/borrowers";
import { queryKeys } from "@/queryKeys";
import type { BorrowerDetail } from "@/types/borrower";
import type { AnalysisOutput, ResolvableCadence } from "@/types/underwriting";

interface AdminAiAnalysisTabProps {
  borrower: BorrowerDetail;
  readOnly?: boolean;
}

function cleanLlmText(text: string | null | undefined): string {
  if (!text) return "";
  return text
    .replace(/\s*\(avg=[^)]+\)/g, "")
    .replace(/\s*\(num=[^)]+\)/g, "")
    .replace(/\s*\(term=[^)]+\)/g, "")
    .replace(/[ ]{2,}/g, " ")
    .trim();
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === "string") return data;
    if (data && typeof data === "object") {
      if ("detail" in data && typeof data.detail === "string") return data.detail;
      return JSON.stringify(data);
    }
    return err.message;
  }
  return String(err);
}

const AdminAiAnalysisTab: React.FC<AdminAiAnalysisTabProps> = ({ borrower, readOnly = false }) => {
  const queryClient = useQueryClient();
  const [persistError, setPersistError] = useState<string | null>(null);
  const [analysisOverride, setAnalysisOverride] = useState<AnalysisOutput | null>(null);

  const latestStatementsDecision = useMemo(() => {
    const decisions = borrower.decisions ?? [];
    const statements = decisions.filter((d) => (d.data_source ?? "statements") === "statements");
    statements.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return statements[0] ?? null;
  }, [borrower.decisions]);

  const latestAnalysis = useMemo(() => {
    if (analysisOverride) return analysisOverride;
    return latestStatementsDecision?.detailed_results?.analysis as AnalysisOutput | undefined;
  }, [analysisOverride, latestStatementsDecision]);

  const persistOverride = async (
    targetKey: string,
    field: "include_in_leverage" | "frequency",
    value: boolean | ResolvableCadence
  ) => {
    setPersistError(null);
    try {
      const result = await borrowerService.saveAnalysisOverride(borrower.id, {
        target_type: "position",
        target_key: targetKey,
        field,
        value,
      });
      if (result.analysis) {
        setAnalysisOverride(result.analysis);
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrower.id) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.analysisRunsList(borrower.id) });
    } catch (err) {
      setPersistError(extractErrorMessage(err));
      throw err;
    }
  };

  if (!latestStatementsDecision) {
    return (
      <Card className="mb-6 border text-center">
        <div className="p-6 text-muted-foreground">
          No statement-based underwriting yet. Upload bank statements to run AI analysis.
        </div>
      </Card>
    );
  }

  return (
    <div className="mb-6">
      {latestAnalysis ? (
        <>
          <FunderAnalysisDashboard
            analysis={latestAnalysis}
            borrowerId={borrower.id}
            transactionSource="statements"
            startDefaultOnboarding
            readOnly={readOnly}
            // Position corrections still persist via the callbacks below when
            // the actor can underwrite (readOnly is false).
            onPersistActiveToggle={
              readOnly
                ? undefined
                : (targetKey, includeInLeverage) =>
                    persistOverride(targetKey, "include_in_leverage", includeInLeverage)
            }
            onPersistFrequency={
              readOnly
                ? undefined
                : (targetKey, frequency) => persistOverride(targetKey, "frequency", frequency)
            }
          />
          {persistError && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-destructive">
              Failed to save correction: {persistError}
            </p>
          )}
        </>
      ) : latestStatementsDecision.llm_analysis ? (
        <Card className="p-4">
          <pre className="whitespace-pre-wrap font-mono text-xs">
            {cleanLlmText(latestStatementsDecision.llm_analysis)}
          </pre>
        </Card>
      ) : null}
    </div>
  );
};

export default AdminAiAnalysisTab;
